import { SignupImpl, type Signup } from "@/domain/usecase/Signup";
import { LoginImpl, type Login } from "@/domain/usecase/Login";
import { LocalStorageSessionStore } from "@/adapters/outbound/persistence/LocalStorageSessionStore";
import { AuthServiceImpl } from "@/adapters/outbound/http/AuthServiceImpl";
import { HttpClient } from "../HttpClient";
import { PersonServiceImpl } from "@/adapters/outbound/http/PersonServiceImpl";
import { makeDataProvider } from "@/adapters/inbound/react_admin/DataProvider";
import type { DataProvider } from "react-admin";
import { ClientServiceImpl } from "@/adapters/outbound/http/ClientServiceImpl";
import { ConstructionSiteServiceImpl } from "@/adapters/outbound/http/ConstructionSiteServiceImpl";
import { JobServiceImpl } from "@/adapters/outbound/http/JobServiceImpl";
import { AddressServiceImpl } from "@/adapters/outbound/http/AddressServiceImpl";
import { JobTrackServiceImpl } from "@/adapters/outbound/http/JobTrackServiceImpl";
import type { JobTrackService } from "@/domain/port/JobTrackService";
import { AssigneesServiceImpl } from "@/adapters/outbound/http/AssigneeServiceImpl";
import { JobReportServiceImpl } from "@/adapters/outbound/http/JobReportServiceImpl";
import type { JobReportService } from "@/domain/port/JobReportService";
import type { JobDetailsService } from "@/domain/port/JobDetailsService";
import { JobDetailsServiceImpl } from "@/adapters/outbound/http/JobDetailsServiceImpl";
import type { PricingService } from "@/domain/port/PricingService";
import { PricingServiceImpl } from "@/adapters/outbound/PricingServiceImpl";
import { RegistrationStoreImpl } from "@/adapters/outbound/persistence/RegistrationStoreImpl";
import type { RegistrationStore } from "@/domain/port/RegistrationStore";
import type { RegistrationService } from "@/domain/port/RegistrationService";
import { RegistrationServiceImpl } from "@/adapters/outbound/http/RegistrationServiceImpl";
import { CompleteRegistrationAuthentication } from "@/domain/usecase/CompleteRegistrationAuthentication";
import { AccountManagementServiceImpl } from "@/adapters/outbound/http/AccountManagementServiceImpl";
import { IdempotencyKeyFactoryImpl } from "../IdempotencyKeyFactoryImpl";
import { GetAccountBilling } from "@/domain/usecase/GetAccountBilling";
import { IncreaseAccountSeats } from "@/domain/usecase/IncreaseAccountSeats";
import { DecreaseAccountSeats } from "@/domain/usecase/DecreaseAccountSeats";
import { CancelAccountBilling } from "@/domain/usecase/CancelAccountBilling";
import { ReactivateAccountBilling } from "@/domain/usecase/ReactivateAccountBilling";
import { GetBillingPortalLink } from "@/domain/usecase/GetBillingPortalLink";
import { GetAccountSeats } from "@/domain/usecase/GetAccountSeats";
import { InviteAccountUser } from "@/domain/usecase/InviteAccountUser";
import { GrantPersonAccess } from "@/domain/usecase/GrantPersonAccess";
import { RemovePersonSeat } from "@/domain/usecase/RemovePersonSeat";
import { RestorePersonSeat } from "@/domain/usecase/RestorePersonSeat";
import { CreateJobIntake } from "@/domain/usecase/CreateJobIntake";
import { CreateConstructionSiteIntake } from "@/domain/usecase/CreateConstructionSiteIntake";
import { ResendAccountInvitation } from "@/domain/usecase/ResendAccountInvitation";
import { RevokeInvitation } from "@/domain/usecase/RevokeInvitation";
import { InvitationServiceImpl } from "@/adapters/outbound/http/InvitationServiceImpl";
import { AcceptInvitation } from "@/domain/usecase/AcceptInvitation";
import { PreviewInvitation } from "@/domain/usecase/PreviewInvitation";
import { RequestPasswordReset } from "@/domain/usecase/RequestPasswordReset";
import { ResetPassword } from "@/domain/usecase/ResetPassword";
import { PasswordResetServiceImpl } from "@/adapters/outbound/http/PasswordResetServiceImpl";
import { Logout } from "@/domain/usecase/Logout";
import { ChangeAccountUserRole } from "@/domain/usecase/ChangeAccountUserRole";
import { GetDashboardSnapshot } from "@/domain/usecase/GetDashboardSnapshot";
import { DashboardServiceImpl } from "@/adapters/outbound/http/DashboardServiceImpl";

import { CreateTrialAccount } from "@/domain/usecase/CreateTrialAccount";
import type { SessionStore } from "@/domain/port/SessionStore";

export type AppContainerDeps = {
  sessionStore?: SessionStore;
};

export type AppContainer = {
  createTrialAccount: CreateTrialAccount;
  signup: Signup;
  login: Login;
  getAccountBilling: GetAccountBilling;
  increaseAccountSeats: IncreaseAccountSeats;
  decreaseAccountSeats: DecreaseAccountSeats;
  cancelAccountBilling: CancelAccountBilling;
  reactivateAccountBilling: ReactivateAccountBilling;
  getBillingPortalLink: GetBillingPortalLink;
  getAccountSeats: GetAccountSeats;
  inviteAccountUser: InviteAccountUser;
  grantPersonAccess: GrantPersonAccess;
  removePersonSeat: RemovePersonSeat;
  restorePersonSeat: RestorePersonSeat;
  completeRegistrationAuthentication: CompleteRegistrationAuthentication;
  createJobIntake: CreateJobIntake;
  createConstructionSiteIntake: CreateConstructionSiteIntake;
  dataProvider: DataProvider;
  jobTrackService: JobTrackService;
  jobReportService: JobReportService;
  jobDetailsService: JobDetailsService;
  pricingService: PricingService;
  registrationStore: RegistrationStore;
  registrationService: RegistrationService;
  resendAccountInvitation: ResendAccountInvitation;
  revokeInvitation: RevokeInvitation;
  previewInvitation: PreviewInvitation;
  acceptInvitation: AcceptInvitation;
  requestPasswordReset: RequestPasswordReset;
  resetPassword: ResetPassword;
  logout: Logout;
  changeAccountUserRole: ChangeAccountUserRole;
  getDashboardSnapshot: GetDashboardSnapshot;
};

export function createContainer(
  deps: AppContainerDeps = {},
): AppContainer {
  const session = deps.sessionStore ?? new LocalStorageSessionStore();
  const registrationStore = new RegistrationStoreImpl();
  const idempotencyKeyFactory = new IdempotencyKeyFactoryImpl();

  const httpClient = new HttpClient(session);

  const authService = new AuthServiceImpl(httpClient);
  const personService = new PersonServiceImpl(httpClient);
  const clientService = new ClientServiceImpl(httpClient);
  const constructionSiteService = new ConstructionSiteServiceImpl(httpClient);
  const jobService = new JobServiceImpl(httpClient);
  const addressService = new AddressServiceImpl(httpClient);
  const assigneesService = new AssigneesServiceImpl(httpClient);
  const jobTrackService = new JobTrackServiceImpl(httpClient);
  const jobReportService = new JobReportServiceImpl(httpClient);
  const jobDetailsService = new JobDetailsServiceImpl(httpClient);
  const pricingService = new PricingServiceImpl(1200, 0.19);
  const registrationService = new RegistrationServiceImpl(httpClient);
  const accountManagementService = new AccountManagementServiceImpl(httpClient);
  const invitationtService = new InvitationServiceImpl(httpClient);
  const passwordResetService = new PasswordResetServiceImpl(httpClient);
  const dashboardService = new DashboardServiceImpl(httpClient);

  const createTrialAccount = new CreateTrialAccount(registrationService, session);
  const signup = new SignupImpl(authService, session);
  const login = new LoginImpl(authService, session);
  const completeRegistrationAuthentication =
    new CompleteRegistrationAuthentication(session, registrationStore);
  const getAccountBilling = new GetAccountBilling(accountManagementService);
  const increaseAccountSeats = new IncreaseAccountSeats(
    accountManagementService,
    idempotencyKeyFactory,
  );
  const decreaseAccountSeats = new DecreaseAccountSeats(
    accountManagementService,
    idempotencyKeyFactory,
  );
  const cancelAccountBilling = new CancelAccountBilling(
    accountManagementService,
    idempotencyKeyFactory,
  );
  const reactivateAccountBilling = new ReactivateAccountBilling(
    accountManagementService,
    idempotencyKeyFactory,
  );
  const getBillingPortalLink = new GetBillingPortalLink(
    accountManagementService,
  );
  const getAccountSeats = new GetAccountSeats(accountManagementService);
  const inviteAccountUser = new InviteAccountUser(accountManagementService);
  const grantPersonAccess = new GrantPersonAccess(accountManagementService);
  const removePersonSeat = new RemovePersonSeat(accountManagementService);
  const restorePersonSeat = new RestorePersonSeat(accountManagementService);
  const createJobIntake = new CreateJobIntake(jobService);
  const createConstructionSiteIntake = new CreateConstructionSiteIntake(constructionSiteService);
  const resendAccountInvitation = new ResendAccountInvitation(accountManagementService);
  const revokeInvitation = new RevokeInvitation(accountManagementService);
  const acceptInvitation = new AcceptInvitation(invitationtService, session);
  const previewInvitation = new PreviewInvitation(invitationtService);
  const requestPasswordReset = new RequestPasswordReset(passwordResetService);
  const resetPassword = new ResetPassword(passwordResetService);
  const logout = new Logout(authService, session);
  const changeAccountUserRole = new ChangeAccountUserRole(accountManagementService);
  const getDashboardSnapshot = new GetDashboardSnapshot(dashboardService);

  const dataProvider = makeDataProvider({
    persons: personService,
    clients: clientService,
    constructionSites: constructionSiteService,
    jobs: jobService,
    addresses: addressService,
    assignees: assigneesService,
    instrument: ({ op, resource, ms, err }) =>
      console.log(`[ra] ${resource}.${op} ${ms}ms${err ? " ERROR" : ""}`),
  });

  return {
    createTrialAccount,
    signup,
    login,
    getAccountBilling,
    increaseAccountSeats,
    decreaseAccountSeats,
    cancelAccountBilling,
    reactivateAccountBilling,
    getBillingPortalLink,
    getAccountSeats,
    inviteAccountUser,
    grantPersonAccess,
    removePersonSeat,
    restorePersonSeat,
    completeRegistrationAuthentication,
    createJobIntake,
    createConstructionSiteIntake,
    dataProvider,
    jobTrackService,
    jobReportService,
    jobDetailsService,
    pricingService,
    registrationStore,
    registrationService,
    resendAccountInvitation,
    revokeInvitation,
    previewInvitation,
    acceptInvitation,
    requestPasswordReset,
    resetPassword,
    logout,
    changeAccountUserRole,
    getDashboardSnapshot
  };
}
